import { ApolloError } from '@apollo/client'
import { MergeGuestSessionMutation } from '__generated__/graphql'
import { NotificationState } from 'features/notification/types'
import type { GuestDemoRounds } from '../store'

type ClaimResult = MergeGuestSessionMutation['mergeGuestSession']
type ModeResult = Pick<
  ClaimResult['upDownAi'],
  'roundsMigrated' | 'scoreMigrated' | 'rankDaily'
>

export type ClaimToast = Pick<
  NotificationState,
  'type' | 'title' | 'description'
>

const MODE_LABELS: Record<keyof GuestDemoRounds, string> = {
  upDownAi: 'Human vs AI',
  bullseye: "Bull's Eye"
}

const plural = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`

const formatScore = (score: number) =>
  `${score > 0 ? '+' : ''}${Number.isInteger(score) ? score : score.toFixed(1)}`

export const countRounds = (rounds: GuestDemoRounds) =>
  rounds.upDownAi + rounds.bullseye

/** "Human vs AI: 3 rounds · Bull's Eye: 1 round"; empty when nothing was played. */
export const describeDemoRounds = (rounds: GuestDemoRounds) =>
  (Object.keys(MODE_LABELS) as Array<keyof GuestDemoRounds>)
    .filter(mode => rounds[mode] > 0)
    .map(mode => `${MODE_LABELS[mode]}: ${plural(rounds[mode], 'round')}`)
    .join(' · ')

const describeMode = (label: string, mode: ModeResult) => {
  if (mode.roundsMigrated === 0) return null

  return [
    `${label}: ${plural(mode.roundsMigrated, 'round')}`,
    mode.scoreMigrated ? `score ${formatScore(mode.scoreMigrated)}` : null,
    mode.rankDaily != null ? `daily rank #${mode.rankDaily}` : null
  ]
    .filter(Boolean)
    .join(', ')
}

/**
 * What the toast says after a claim. A result short of the rounds the guest
 * played is a partial success: the reasons arrive with BE-312, until then
 * the numbers have to do.
 */
export const describeClaimResult = (
  result: ClaimResult,
  rounds: GuestDemoRounds
): ClaimToast => {
  const played = countRounds(rounds)
  const isPartial = played > 0 && result.roundsMigrated < played
  const lines = [
    describeMode(MODE_LABELS.upDownAi, result.upDownAi),
    describeMode(MODE_LABELS.bullseye, result.bullseye),
    describeMode('Up/Down', result.upDown)
  ].filter(Boolean)

  return {
    type: isPartial ? 'warning' : 'success',
    title:
      isPartial ?
        `Demo results partly claimed: ${result.roundsMigrated} of ${played} rounds`
      : 'Demo results claimed',
    description:
      lines.join(' · ') ||
      (isPartial ?
        'The other rounds were rejected or had expired.'
      : 'Your demo results are on the leaderboards.')
  }
}

export const getClaimErrorMessage = (error: unknown) => {
  const message =
    error instanceof ApolloError ?
      (error.graphQLErrors[0]?.message ?? error.message)
    : error instanceof Error ? error.message
    : ''

  return message || 'Something went wrong. Your demo results are kept.'
}
